<template>
  <form @submit.prevent="handleSubmit">
    <p class="correcao-resumo">{{ resumo }}</p>

    <template v-if="acao === 'ajustar'">
      <div class="form-field">
        <select-list
          elem-id="comboAjusteDirection"
          data-testid="cliente-combo-ajuste-direction"
          v-model="formData.direction"
          :itens-select="directionOptions"
          :label="t('Tipo do ajuste')"
          :auto-select-first="false"
        />
        <small v-if="errors.direction" class="error-message">{{ errors.direction }}</small>
      </div>

      <div class="form-field">
        <input-number
          elem-id="comboAjusteQuantity"
          data-testid="cliente-combo-ajuste-quantity"
          v-model="formData.quantity"
          :label="t('Pares')"
          :min="1"
          :maxFractionDigits="0"
          fluid
        />
        <small v-if="errors.quantity" class="error-message">{{ errors.quantity }}</small>
      </div>
    </template>

    <div class="form-field">
      <input-text-area
        elem-id="comboCorrecaoMotivo"
        data-testid="cliente-combo-correcao-motivo"
        v-model="formData.motivo"
        :label="t('Motivo')"
        :placeholder="t('Fica registrado no histórico da compra')"
        required
      />
      <small v-if="errors.motivo" class="error-message">{{ errors.motivo }}</small>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useClienteCombos } from '../../composables/useClienteCombos';
import { emitValidationFailed } from '../../composables/useFormValidation';
import { eventBus } from '../../eventBus';
import { error as logError } from '../../utils/DebugLogger';

const props = withDefaults(defineProps<{
  acao: 'estornar' | 'ajustar' | 'cancelar';
  purchaseId: number;
  comboNome: string;
  movimentoId?: number | null;
}>(), { movimentoId: null });

const emit = defineEmits<{
  'submit-success': [];
}>();

const { t } = useI18n();
const { estornar, ajustar, cancelarCompra } = useClienteCombos();

const formData = reactive({
  direction: undefined as string | undefined,
  quantity: undefined as number | undefined,
  motivo: '',
});

const errors = reactive({
  direction: '',
  quantity: '',
  motivo: '',
});

// Idempotência do ajuste: a mesma chave em todo clique deste formulário, para
// um duplo clique ou uma repetição depois de queda de rede não ajustar duas vezes.
const idempotencyKey = crypto.randomUUID();

const directionOptions = computed(() => [
  { ID: 'credito', Name: t('Crédito (+) soma pares') },
  { ID: 'debito', Name: t('Débito (−) tira pares') },
]);

const resumo = computed<string>(() => {
  if (props.acao === 'estornar') {
    return `${props.comboNome}: ${t('devolve 1 par ao saldo. O consumo original continua no histórico.')}`;
  }
  if (props.acao === 'cancelar') {
    return `${props.comboNome}: ${t('anula o saldo restante e não tem volta. A devolução do dinheiro é feita fora do sistema.')}`;
  }
  return `${props.comboNome}: ${t('soma ou tira pares do saldo. O débito não leva o disponível abaixo de zero.')}`;
});

const validateForm = (): boolean => {
  errors.motivo = formData.motivo.trim() ? '' : t('Informe o motivo');
  if (props.acao === 'ajustar') {
    errors.direction = formData.direction ? '' : t('Campo obrigatório');
    errors.quantity = Number.isInteger(formData.quantity) && Number(formData.quantity) >= 1
      ? ''
      : t('Informe pelo menos 1 par');
  }

  const isValid = !errors.motivo && !errors.direction && !errors.quantity;
  if (!isValid) {
    emitValidationFailed('ClienteComboCorrecaoForm', errors);
  }
  return isValid;
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm()) return;

  const motivo = formData.motivo.trim();
  try {
    if (props.acao === 'estornar') {
      await estornar({ movement_id: Number(props.movimentoId), motivo });
      eventBus.emit('globalMessage.success', t('Consumo estornado'));
    } else if (props.acao === 'ajustar') {
      await ajustar({
        purchase_id: props.purchaseId,
        direction: String(formData.direction),
        quantity: Number(formData.quantity),
        motivo,
        idempotency_key: idempotencyKey,
      });
      eventBus.emit('globalMessage.success', t('Saldo do combo ajustado'));
    } else {
      await cancelarCompra({ purchase_id: props.purchaseId, motivo });
      eventBus.emit('globalMessage.success', t('Compra do combo cancelada'));
    }
    emit('submit-success');
  } catch (error: unknown) {
    // A frase da recusa é do backend ("Este consumo ja foi estornado", par reservado…).
    logError('ClienteComboCorrecaoForm', 'handleSubmit', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', message || t('Erro ao gravar a correção do combo'));
  }
};

defineExpose({ handleSubmit, validateForm });
</script>

<style scoped>
.correcao-resumo {
  margin: 0 0 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
